import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Club, Member, RsvpStatus } from "@pagemates/shared";
import { askForPush } from "./PushAsk";
import { api, describeError } from "../lib/api";
import { Avatar } from "./ui";
import { ProfileFace } from "./Profile";
import { CalendarIcon, CheckIcon, CloseIcon, PencilIcon } from "./Icons";

const DAY = 86_400_000;
const when = (iso: string) => new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
export function countdown(iso: string): string {
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / DAY);
  if (days < -1) return `${-days} days ago`;
  if (days === -1) return "yesterday";
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} days`;
}

/** The next meeting: when, where, who hosts, your RSVP, who's going. Admins edit it in place. */
export function MeetingCard({ club, members, isAdmin, agendaLink = false, hero = false }: { club: Club; members: Member[]; isAdmin: boolean; agendaLink?: boolean; hero?: boolean }) {
  const qc = useQueryClient();
  const m = club.meeting;
  const [editing, setEditing] = useState(false);
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ["club"] }), qc.invalidateQueries({ queryKey: ["meeting-detail"] })]);
  // The first answer is the moment to offer a reminder the day before (askForPush only asks once, ever).
  const rsvp = useMutation({ mutationFn: (status: RsvpStatus) => api.setRsvp(club.id, status), onSuccess: (_d, status) => { if (status !== "cant") askForPush("rsvp"); return refresh(); } });
  if (!m && !isAdmin) return hero
    ? <div className="mhero"><div className="date">No date yet</div><div className="sub">An admin sets the next meeting. The agenda builds itself meanwhile.</div></div>
    : <section className="card quiet-card"><CalendarIcon /><div><b>No meeting on the calendar</b><div className="d">An admin sets the next one here.</div></div></section>;
  if (!m || editing) return <MeetingEditor club={club} members={members} onDone={() => setEditing(false)} />;
  const past = new Date(m.at).getTime() < Date.now();
  const stale = m.rsvp.mine?.stale;
  const btn = (s: RsvpStatus, label: string) => (
    <button type="button" className={`btn sm${m.rsvp.mine?.status === s && !stale ? " on" : ""}`} disabled={rsvp.isPending} onClick={() => rsvp.mutate(s)} aria-pressed={m.rsvp.mine?.status === s && !stale}>
      {m.rsvp.mine?.status === s && !stale && <CheckIcon width={14} height={14} />}{label}
    </button>
  );
  if (hero) {
    return (
      <section className="meet mhero">
        {isAdmin && <button type="button" className="pencil" aria-label="Edit the meeting" onClick={() => setEditing(true)}><PencilIcon width={15} height={15} /><span aria-hidden="true">Edit</span></button>}
        <span className="tile" aria-hidden="true"><i>{new Date(m.at).toLocaleDateString(undefined, { month: "short" })}</i><b>{new Date(m.at).getDate()}</b></span>
        <div style={{ minWidth: 0 }}>
          <div className="date">{new Date(m.at).toLocaleDateString(undefined, { weekday: "long" })} · {new Date(m.at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</div>
          <div className="sub">{countdown(m.at)}{m.location ? ` · ${m.location}` : ""}{m.host ? ` · ${m.host.name} is hosting` : ""}</div>
        </div>
        {m.previousAt && !past && stale && m.rsvp.mine && (
          <div className="flagline">
            <span>The date moved from {when(m.previousAt)}. You said <b>{{ going: "going", cant: "can't", maybe: "maybe" }[m.rsvp.mine.status]}</b>. Still true?</span>
            <button type="button" className="btn sm" disabled={rsvp.isPending} onClick={() => rsvp.mutate(m.rsvp.mine!.status)}>Yes, still {{ going: "going", cant: "can't", maybe: "maybe" }[m.rsvp.mine.status]}</button>
          </div>
        )}
        {!past ? (
          <div className="rsvp">
            {btn("going", "Going")}{btn("cant", "Can't")}{btn("maybe", "Maybe")}
            <span className="faces">{m.rsvp.goingMembers.slice(0, 6).map((u) => <ProfileFace key={u.id} user={u} />)}</span>
            <span className="small muted">{m.rsvp.going} going{m.rsvp.maybe ? ` · ${m.rsvp.maybe} maybe` : ""}</span>
          </div>
        ) : <div className="small muted">This meeting has passed.</div>}
        {rsvp.isError && <p className="error small">{describeError(rsvp.error)}</p>}
      </section>
    );
  }
  return (
    <section className="card meet">
      {isAdmin && <button type="button" className="pencil" aria-label="Edit the meeting" onClick={() => setEditing(true)}><PencilIcon width={15} height={15} /><span aria-hidden="true">Edit</span></button>}
      <div>
        <div className="when">{when(m.at)}</div>
        <div className="d">{countdown(m.at)}{m.location ? ` · ${m.location}` : ""}</div>
      </div>
      {m.previousAt && !past && stale && m.rsvp.mine && (
          <div className="flagline">
            <span>The date moved from {when(m.previousAt)}. You said <b>{{ going: "going", cant: "can't", maybe: "maybe" }[m.rsvp.mine.status]}</b>. Still true?</span>
            <button type="button" className="btn sm" disabled={rsvp.isPending} onClick={() => rsvp.mutate(m.rsvp.mine!.status)}>Yes, still {{ going: "going", cant: "can't", maybe: "maybe" }[m.rsvp.mine.status]}</button>
          </div>
        )}
      {m.host && <div className="host"><Avatar name={m.host.name} image={m.host.image} /><span>{m.host.name} is hosting</span></div>}
      {!past && (
        <div className="rsvp">
          {btn("going", "Going")}{btn("cant", "Can't")}{btn("maybe", "Maybe")}
          <span className="faces">{m.rsvp.goingMembers.slice(0, 5).map((u) => <ProfileFace key={u.id} user={u} />)}</span>
        </div>
      )}
      <div className="small muted">
        {past ? "This meeting has passed." : `${m.rsvp.going} going${m.rsvp.maybe ? ` · ${m.rsvp.maybe} maybe` : ""}${m.rsvp.staleYes ? ` · ${m.rsvp.staleYes} said yes before the change` : ""}`}
        {agendaLink && <>{" · "}<Link to="/clubs/$clubId/meeting" params={{ clubId: club.slug }} search={m.clubBookId ? { book: m.clubBookId } : {}}>Open the agenda ›</Link></>}
      </div>
      {rsvp.isError && <p className="error small">{describeError(rsvp.error)}</p>}
    </section>
  );
}

function toLocalInput(iso: string) {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function MeetingEditor({ club, members, onDone }: { club: Club; members: Member[]; onDone: () => void }) {
  const qc = useQueryClient();
  const m = club.meeting;
  const [at, setAt] = useState(m ? toLocalInput(m.at) : "");
  const [location, setLocation] = useState(m?.location ?? "");
  const [hostId, setHostId] = useState(m?.host?.id ?? "");
  const [noDate, setNoDate] = useState(false); // Save is always a real button; a missing date is said on submit
  const save = useMutation({
    mutationFn: (input: { at: string | null; location?: string | null; hostId?: string | null }) => api.setMeeting(club.id, input),
    onSuccess: async () => { await Promise.all([qc.invalidateQueries({ queryKey: ["club"] }), qc.invalidateQueries({ queryKey: ["meeting-detail"] }), qc.invalidateQueries({ queryKey: ["questions"] })]); onDone(); },
  });
  const moving = m && at && new Date(at).toISOString() !== m.at && new Date(m.at) > new Date();
  return (
    <form className="card stack meet-edit" noValidate onSubmit={(e) => { e.preventDefault(); if (!at) { setNoDate(true); document.getElementById("m-at")?.focus(); return; } save.mutate({ at: new Date(at).toISOString(), location, hostId: hostId || null }); }}>
      <div className="row spread"><b>{m ? "Edit the meeting" : "Next meeting"}</b>{m && <button type="button" className="linkish small muted" onClick={onDone}><CloseIcon width={16} height={16} /></button>}</div>
      <div className="field"><label htmlFor="m-at">When (your local time)</label><input id="m-at" className={`input${noDate ? " err" : ""}`} type="datetime-local" value={at} aria-invalid={noDate} onChange={(e) => { setAt(e.target.value); setNoDate(false); }} />
        {noDate && <span className="small warn" role="alert">Pick a date and time.</span>}</div>
      <div className="field"><label htmlFor="m-loc">Where, or a call link (optional)</label><input id="m-loc" className="input" value={location} maxLength={120} placeholder="A café, or a call link" onChange={(e) => setLocation(e.target.value)} /></div>
      <div className="field"><label htmlFor="m-host">Host (optional)</label>
        <select id="m-host" className="input" value={hostId} onChange={(e) => setHostId(e.target.value)}>
          <option value="">Nobody in particular</option>
          {members.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select></div>
      {moving && <p className="muted small">Moving the date asks everyone who said yes to confirm again. Questions stay.</p>}
      <div className="row" style={{ flexWrap: "wrap" }}>
        <button className="btn primary" type="submit" disabled={save.isPending}>Save</button>
        {m && <button className="btn ghost" type="button" disabled={save.isPending} onClick={() => save.mutate({ at: null })}>Clear the date</button>}
        {save.isError && <p className="error">{describeError(save.error)}</p>}
      </div>
    </form>
  );
}

